import { http } from '../client'
import { USE_MOCKS } from '../config'
import { endpoints } from '../endpoints'
import { banners, categories, products, toSummary } from '../mock/fixtures'
import { delay, mockDb } from '../mock/db'
import { ApiError } from '../types'
import type {
  Category,
  HomeContent,
  Paginated,
  Product,
  ProductSummary,
  SearchParams,
} from '../types'

/* ------------------------------------------------------------------ *
 * Home
 * ------------------------------------------------------------------ */

export function getHome(): Promise<HomeContent> {
  if (!USE_MOCKS) return http.get<HomeContent>(endpoints.home)

  const byCategory = (categoryId: string) =>
    products.filter((p) => p.categoryId === categoryId).map(toSummary)

  const content: HomeContent = {
    banners,
    dailyOffer: {
      label: null,
      product: toSummary(products.find((p) => p.id === 'p-cameras-ip') ?? products[0]),
    },
    shelves: [
      {
        id: 'shelf-ofertas',
        title: 'Ofertas',
        seeAllLabel: 'Ver todas as ofertas',
        seeAllHref: '/busca?ordenar=discount',
        products: products
          .filter((p) => (p.discountPercent ?? 0) > 0)
          .slice(0, 10)
          .map(toSummary),
      },
      {
        id: 'shelf-eletronicos',
        title: 'Mais vendidos da semana em Eletrônicos',
        seeAllLabel: 'Ver lista completa',
        seeAllHref: '/categoria/eletronicos',
        products: byCategory('cat-eletronicos'),
      },
      {
        id: 'shelf-esportes',
        title: 'Mais vendidos da semana em Esportes e Fitness',
        seeAllLabel: 'Ver lista completa',
        seeAllHref: '/categoria/esportes-e-fitness',
        products: byCategory('cat-esportes'),
      },
      {
        id: 'shelf-beleza',
        title: 'Mais vendidos em Beleza e Cuidados Pessoais',
        seeAllLabel: 'Ver lista completa',
        seeAllHref: '/categoria/beleza-e-cuidado-pessoal',
        products: byCategory('cat-beleza'),
      },
    ],
  }

  return delay(content, 420)
}

/* ------------------------------------------------------------------ *
 * Categorias
 * ------------------------------------------------------------------ */

export function getCategories(): Promise<Category[]> {
  if (!USE_MOCKS) return http.get<Category[]>(endpoints.categories)
  return delay(categories, 120)
}

/* ------------------------------------------------------------------ *
 * Busca
 * ------------------------------------------------------------------ */

const PAGE_SIZE = 12

export function searchProducts(params: SearchParams): Promise<Paginated<ProductSummary>> {
  if (!USE_MOCKS) {
    return http.get<Paginated<ProductSummary>>(endpoints.products.search, {
      query: {
        q: params.q,
        categoryId: params.categoryId,
        sort: params.sort,
        cursor: params.cursor,
        limit: params.limit ?? PAGE_SIZE,
      },
    })
  }

  const term = params.q?.trim().toLowerCase() ?? ''
  let result = mockDb.allProducts()

  if (term) {
    result = result.filter(
      (p) =>
        p.title.toLowerCase().includes(term) ||
        (p.categoryName?.toLowerCase().includes(term) ?? false),
    )
  }
  if (params.categoryId) {
    result = result.filter((p) => p.categoryId === params.categoryId)
  }

  switch (params.sort) {
    case 'price_asc':
      result = [...result].sort((a, b) => a.price - b.price)
      break
    case 'price_desc':
      result = [...result].sort((a, b) => b.price - a.price)
      break
    case 'discount':
      result = [...result].sort(
        (a, b) => (b.discountPercent ?? 0) - (a.discountPercent ?? 0),
      )
      break
    default:
      result = [...result].sort((a, b) => (b.soldCount ?? 0) - (a.soldCount ?? 0))
  }

  const limit = params.limit ?? PAGE_SIZE
  const start = params.cursor ? Number(params.cursor) : 0
  const page = result.slice(start, start + limit)
  const nextStart = start + limit

  return delay(
    {
      items: page.map(toSummary),
      nextCursor: nextStart < result.length ? String(nextStart) : null,
      total: result.length,
    },
    380,
  )
}

/* ------------------------------------------------------------------ *
 * Detalhe
 * ------------------------------------------------------------------ */

export function getProduct(slugOrId: string): Promise<Product> {
  if (!USE_MOCKS) return http.get<Product>(endpoints.products.detail(slugOrId))

  const found = mockDb
    .allProducts()
    .find((p) => p.slug === slugOrId || p.id === slugOrId)

  if (!found) {
    return Promise.reject(
      new ApiError('Produto não encontrado.', 404, 'product_not_found'),
    )
  }
  return delay(found, 260)
}
